import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, useMediaQuery, useTheme } from '@material-ui/core'
import React, { useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits } from '../../assets/utils'
import { useReportFilter, ReportToolbar } from '../../assets/reportFilters'
import { exportExcel, reportMetaNotes, fetchReportQr } from '../../assets/excelUtils'
import { documentsState } from '../../pages/contractor/atoms'
import jspdf from 'jspdf'
import 'jspdf-autotable'
import { vazirFont } from '../../assets/fonts/vazir-font-v18.0.0/Vazir'
import { titrFont } from '../../assets/fonts/Titr/Titr'
import api from '../../api/client';
import { LoadMoreButton, useIncrementalList } from '../../hooks/useIncrementalList'
import { userInfoState } from '../../recoil/atoms'
import { mitraFont } from '../../assets/fonts/Mitra/Mitra'
import { addPdfFooter } from '../../assets/reportFooter'

const printDetails = async (id, number, date, contract, userInfo, documents) => {
  const docItems = Array.isArray(documents?.data) ? documents.data : Array.isArray(documents) ? documents : []
  const rows = docItems.filter(item => item.number === number && item.date === date)
  const document_cheque_data = (await api.get(`contracts/documents/document_cheque/${id}/`)).data

  const doc = new jspdf()
  doc.addFileToVFS('Vazir.ttf', vazirFont)
  doc.addFont('Vazir.ttf', 'Vazir', 'normal')
  doc.addFileToVFS('Titr.ttf', titrFont)
  doc.addFont('Titr.ttf', 'Titr', 'normal')
  doc.addFileToVFS('Mitra.ttf', mitraFont)
  doc.addFont('Mitra.ttf', 'Mitra', 'normal')

  doc.setFont('Titr')
  doc.setFontSize(15)

  const textHeight = doc.getTextDimensions('ب').h
  let currentHeight = 5 * textHeight
  const title = contract.contractee
  doc.text(title, doc.internal.pageSize.width / 2, currentHeight, 'center')
  currentHeight += textHeight

  doc.setFont('Mitra')
  doc.setFontSize(12)
  const contractorName = userInfo.name
  doc.text(contractorName, 200, currentHeight, 'right')
  currentHeight += textHeight

  let contractTitle = 'قرارداد:  '
  contractTitle += 'شماره  ' + toFarsiDigits(contract.number)
  contractTitle += '  تاریخ  ' + gregorianToJalali(contract.date)
  doc.text(contractTitle, 200, currentHeight, 'right')
  currentHeight += textHeight

  const subject = toFarsiDigits(contract.subject)
  doc.text(subject, 200, currentHeight, 'right')
  currentHeight += textHeight

  let documentNumber = 'شماره سند:  ' + toFarsiDigits(number)
  documentNumber += '       تاریخ:  ' + gregorianToJalali(date)
  doc.text(documentNumber, 200, currentHeight, 'right')
  currentHeight += textHeight

  let text = 'شرح سند: ' + (rows[0]?.document_remark || '')
  doc.text(text, 200, currentHeight, 'right')
  currentHeight += textHeight

  const body = []
  for (let i = 0; i < rows.length; i++) {
    body.push([
      toFarsiDigits(i + 1),
      toFarsiDigits(rows[i].account_code),
      toFarsiDigits(rows[i].account_remark),
      toFarsiDigits(rows[i].detail_remark),
      toFarsiDigits(thousandsSeparate(rows[i].debtor)),
      toFarsiDigits(thousandsSeparate(rows[i].creditor))
    ].reverse())
  }

  doc.autoTable({
    styles: { font: 'Mitra', halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: 0 },
    head: [[
      'ردیف',
      'کد حساب',
      'عنوان حساب',
      'شرح',
      'بدهکار',
      'بستانکار'
    ].reverse()],
    body: body,
    margin: { top: currentHeight, left: 10, right: 10 },
    theme: 'grid',
    headStyles: { font: 'Mitra', fontSize: 10, halign: 'center', fillColor: 255, textColor: 0, fontStyle: 'normal' },
    bodyStyles: { font: 'Mitra', fontSize: 10, halign: 'center', fillColor: 255, textColor: 0, fontStyle: 'normal' }
  })

  currentHeight = doc.autoTable.previous.finalY + textHeight
  const desc = 'جمع پرداختی ، مجموع خالص و مالیات بر ارزش افزوده است'
  doc.text(desc, 200, currentHeight, 'right')

  const copyright = 'سامانه ارجان پیمان - شرکت ارجان آرایه پویا'
  doc.text(copyright, 200, currentHeight, 'left')
  currentHeight += 3 * textHeight

  if (document_cheque_data.length > 0) {
    doc.setFont('Titr')
    doc.setFontSize(12)
    let t = 'چک های صادر شده'
    doc.text(t, doc.internal.pageSize.width / 2, currentHeight, 'center')
    currentHeight += textHeight

    doc.autoTable({
      styles: { font: 'Mitra', halign: 'center', valign: 'middle', lineWidth: 0.5, lineColor: 0 },
      head: [[
        'ردیف',
        'شماره چک',
        'تاریخ صدور',
        'تاریخ وصول',
        'مبلغ',
        'گیرنده',
        'شماره حساب',
        'شناسه پرداخت'
      ].reverse()],
      body: document_cheque_data.map((item, index) => [
        toFarsiDigits(index + 1),
        toFarsiDigits(item.number),
        gregorianToJalali(item.cheque_date),
        gregorianToJalali(item.cheque_collected_date),
        toFarsiDigits(thousandsSeparate(item.amount)),
        toFarsiDigits(item.receiver),
        toFarsiDigits(item.account_number),
        toFarsiDigits(item.transaction_number)
      ].reverse()),
      margin: { left: 10, right: 10 },
      startY: currentHeight,
      theme: 'grid',
      headStyles: { font: 'Mitra', fontSize: 10, halign: 'center', fillColor: 255, textColor: 0, fontStyle: 'normal' },
      bodyStyles: { font: 'Mitra', fontSize: 10, halign: 'center', fillColor: 255, textColor: 0, fontStyle: 'normal' }
    })
    doc.setFont('Mitra')
    currentHeight = doc.autoTable.previous.finalY + textHeight
    t = 'جمع مبالغ چک های صادر شده: ' + toFarsiDigits(thousandsSeparate(document_cheque_data.reduce((acc, item) => acc + item.amount, 0))) + ' ریال'
    doc.text(t, 200, currentHeight, 'right')
    currentHeight += textHeight
  }

  currentHeight = doc.autoTable.previous.finalY + textHeight
  currentHeight += 2 * textHeight
  doc.setFont('Titr')
  doc.setFontSize(12)
  text = 'بدون مهر ' + contract.contractee + ' فاقد ارزش استنادی است'
  doc.text(text, doc.internal.pageSize.width / 2, currentHeight, 'center')

  addPdfFooter(doc)
  doc.output('dataurlnewwindow', 'details.pdf')
}

const Documents = (props) => {
  const recoilDocs = useRecoilValue(documentsState)
  const userInfo = useRecoilValue(userInfoState)
  const contract = props.contract
  const docsArray = props.documents
    ? (Array.isArray(props.documents?.data) ? props.documents.data : props.documents)
    : (Array.isArray(recoilDocs?.data) ? recoilDocs.data : [])

  const unique_documents = docsArray.reduce((acc, cur) => {
    if (!acc.find(item => item.number === cur.number && item.date === cur.date)) acc.push(cur)
    return acc
  }, [])

  const extract = useMemo(() => ({
    year: (row) => row.fiscal_year,
    search: (row) => [
      row.number, row.document_remark, row.fiscal_year, row.transfer_number,
      row.transfer_fiscal_year, row.request_number, row.request_type,
    ],
  }), [])
  const { search, setSearch, year, setYear, years, filtered } = useReportFilter(unique_documents, extract)

  // رندرِ تدریجی + حذفِ جعبهٔ اسکرولِ تودرتو در موبایل
  const { visible, hasMore, loadMore, remaining } = useIncrementalList(filtered, {
    step: 50,
    resetKey: `${search}|${year}`,
  })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // خروجی اکسل اسناد — همان ستونهای جدول + اعداد واقعی
  const exportDocumentsExcel = async () => {
    const qr = contract && contract.id ? await fetchReportQr(contract.id) : null
    exportExcel({
      filename: 'documents.xlsx',
      title: `اسناد حسابداری — ${contract && contract.contractee ? contract.contractee : ''}${contract && contract.number ? ' | قرارداد ' + contract.number : ''}`,
      headers: ['ردیف', 'سال مالی سند', 'شماره سند', 'شرح سند', 'تاریخ سند', 'سال مالی حواله', 'شماره حواله', 'تاریخ حواله', 'شماره صورت وضعیت', 'نوع صورت وضعیت'],
      rows: filtered.map((row, index) => [
        index + 1,
        row.fiscal_year,
        row.number,
        row.document_remark,
        gregorianToJalali(row.date),
        row.transfer_fiscal_year,
        row.transfer_number,
        gregorianToJalali(row.transfer_date),
        row.request_number,
        row.request_type,
      ]),
      notes: reportMetaNotes(),
      qrDataUri: qr,
    })
  }

  return (
    <Box>
      {docsArray.length > 0 && (
        <>
          <ReportToolbar
            search={search} setSearch={setSearch}
            year={year} setYear={setYear} years={years}
            rowsCount={unique_documents.length} filteredCount={filtered.length}
          />
          <Box style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <Button variant='contained' color='secondary' onClick={exportDocumentsExcel}>
              دانلود اکسل
            </Button>
          </Box>
        </>
      )}
      <TableContainer
        elevation={5}
        component={Paper}
        style={isMobile ? undefined : { maxHeight: '450px' }}
      >
      <Table stickyHeader={!isMobile}>
        <TableHead>
          <TableRow>
            <TableCell align='center'><strong>ردیف</strong></TableCell>
            <TableCell align='center'><strong>چاپ</strong></TableCell>
            <TableCell align='center'><strong>سال مالی سند</strong></TableCell>
            <TableCell align='center'><strong>شماره سند</strong></TableCell>
            <TableCell align='center'><strong>شرح سند</strong></TableCell>
            <TableCell align='center'><strong>تاریخ سند</strong></TableCell>
            <TableCell align='center'><strong>سال مالی حواله</strong></TableCell>
            <TableCell align='center'><strong>شماره حواله</strong></TableCell>
            <TableCell align='center'><strong>تاریخ حواله</strong></TableCell>
            <TableCell align='center'><strong>شماره صورت وضعیت</strong></TableCell>
            <TableCell align='center'><strong>نوع صورت وضعیت</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((row, index) => (
            <TableRow key={`${row.number}-${row.date}`}>
              <TableCell align='center'><strong>{toFarsiDigits(index + 1)}</strong></TableCell>
              <TableCell align='center'>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => printDetails(row.id, row.number, row.date, contract, userInfo, docsArray)}
                >
                  چاپ
                </Button>
              </TableCell>
              <TableCell align='center'>{toFarsiDigits(row.fiscal_year)}</TableCell>
              <TableCell align='center'>{toFarsiDigits(row.number)}</TableCell>
              <TableCell align='center'>{toFarsiDigits(row.document_remark)}</TableCell>
              <TableCell align='center'>{gregorianToJalali(row.date)}</TableCell>
              <TableCell align='center'>{toFarsiDigits(row.transfer_fiscal_year)}</TableCell>
              <TableCell align='center'>{toFarsiDigits(row.transfer_number)}</TableCell>
              <TableCell align='center'>{gregorianToJalali(row.transfer_date)}</TableCell>
              <TableCell align='center'>{toFarsiDigits(row.request_number)}</TableCell>
              <TableCell align='center'>{toFarsiDigits(row.request_type)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <LoadMoreButton hasMore={hasMore} remaining={remaining} onClick={loadMore} />
    </Box>
  )
}

export default Documents